/**
 * Copy for the two marketing category hubs (`marketingHubs` namespace):
 * /use-cases and /integrations. Each hub is a crawlable index page — hero,
 * a grid of child links, and a CTA — rendered by the shared
 * <MarketingHubContent>. Page-specific copy for the leaf pages lives in their
 * own fragments (e.g. `uc-ticket-questions.ts`, `intg-slack.ts`).
 */

export const marketingHubsEn = {
  useCases: {
    eyebrow: "Use cases",
    title: "From question to outcome,",
    highlight: "approved by your team",
    subtitle:
      "From a question in Slack to a reviewed fix in your repository: FixControl prepares answers and changes from your tickets, code and conversations — your team approves what goes out, and every decision stays on record.",
    relatedTitle: "Explore the workflows",
    ctaPrimary: "Get started free",
    ctaSecondary: "Book a demo",
    ctaTitle: "See the workflows on your own data",
    ctaDescription:
      "Get started free for support work, or book a guided pilot for the full chain from customer ticket to tested code change and controlled deployment.",
    items: {
      support: {
        title: "Support and email automation",
        desc: "From a question by a customer of your organization to a checked reply, with the right tickets, conversations and documentation.",
      },
      engineering: {
        title: "Bug and code automation",
        desc: "From bug report to a tested change proposal and a pull request after approval.",
      },
      ticketQuestions: {
        title: "Slack & Jira ticket questions",
        desc: "Ask what’s open or waiting for approval, without accidentally filing another ticket.",
      },
      approvedReplies: {
        title: "Approved Freshdesk & Jira replies",
        desc: "Draft with AI, then approve exactly what gets sent to a customer of your organization or as an internal note.",
      },
      remediation: {
        title: "From support ticket to tested fix",
        desc: "Turn a support ticket into a tested code change, delivered as a pull request your team approves — with the test results attached.",
      },
      approvalLedger: {
        title: "AI approval ledger",
        desc: "Every AI-assisted action is recorded: who approved it, from which channel, on which grounds, and what happened.",
      },
      deployment: {
        title: "Controlled deployments",
        desc: "Gate promotions behind human approval, with live rollout status where Argo / Kubernetes is connected.",
      },
      hrGoverned: {
        title: "HR questions with the right permissions",
        desc: "Employees of your organization ask about leave, a payslip or a procedure. A routine answer goes out directly; a sensitive one waits for an assigned reviewer and a second person to approve.",
      },
    },
  },
  integrations: {
    eyebrow: "Integrations",
    title: "Approvals and answers where your team",
    highlight: "already works",
    subtitle:
      "Take in new reports, answer ticket questions, handle approvals and prepare fixes — without switching tools. Every connection uses its own credentials, stays inside your own organization’s data, and shows per part whether it works instead of one green checkmark.",
    relatedTitle: "Browse integrations",
    ctaPrimary: "Get started free",
    ctaSecondary: "Book a demo",
    ctaTitle: "Connect the tools you already run",
    ctaDescription:
      "Connect one integration free to start, or book a demo to see governed reads and writes across your stack.",
    items: {
      slack: {
        title: "Slack",
        desc: "Intake, ticket questions, approvals and decision cards — right in the thread.",
      },
      teams: {
        title: "Microsoft Teams",
        desc: "Intake, questions and approvals from Teams, governed the same way as Slack.",
      },
      jira: {
        title: "Jira",
        desc: "Governed issue reads, transitions, labels and comments, with per-capability health.",
      },
      freshdesk: {
        title: "Freshdesk",
        desc: "Approved customer replies and two-way ticket sync, written through a durable outbox.",
      },
      odoo: {
        title: "Odoo Helpdesk",
        desc: "Tickets created and linked, stages read back, and chatter replies that wait for approval.",
      },
      topdesk: {
        title: "TOPdesk",
        desc: "Incidents created and linked, the caller kept in sync, and replies that wait for approval.",
      },
      afas: {
        title: "AFAS Profit",
        desc: "Read-only HR fields for a governed answer — leave hours and a payroll code, nothing written back.",
      },
      youforce: {
        title: "Visma Raet Youforce",
        desc: "The same read-only HR contract on the Youforce endpoints you expose.",
      },
      github: {
        title: "GitHub",
        desc: "Codebase analysis and AI-proposed patches, delivered as risk-gated pull requests with evidence attached.",
      },
      gitlab: {
        title: "GitLab",
        desc: "The governed patch flow for GitLab repos: analysis, evidence and risk-gated merge requests.",
      },
      argo: {
        title: "Argo & Kubernetes",
        desc: "Rollout status and gated promotion, with promote / abort where configured.",
      },
      fcagent: {
        title: "FC Agent — private infrastructure",
        desc: "Outbound-only connectivity: govern private Kubernetes, Argo, Jenkins and GitLab without exposing them.",
      },
    },
  },
};

export const marketingHubsNl = {
  useCases: {
    eyebrow: "Use-cases",
    title: "Van vraag tot resultaat,",
    highlight: "goedgekeurd door je team",
    subtitle:
      "Van een vraag in Slack tot een gereviewde fix in je repository: FixControl bereidt antwoorden en wijzigingen voor vanuit je tickets, code en gesprekken — je team keurt goed wat vertrekt, en elke beslissing is terug te vinden.",
    relatedTitle: "Verken de workflows",
    ctaPrimary: "Begin gratis",
    ctaSecondary: "Boek een demo",
    ctaTitle: "Zie de workflows op je eigen data",
    ctaDescription:
      "Begin gratis met supportwerk, of boek een begeleide pilot voor de hele keten van klantticket tot geteste codewijziging en gecontroleerde deployment.",
    items: {
      support: {
        title: "Support- en e-mailautomatisering",
        desc: "Van een vraag van een klant van je organisatie naar een gecontroleerd antwoord, met de juiste tickets, gesprekken en documentatie.",
      },
      engineering: {
        title: "Bug- en codeautomatisering",
        desc: "Van bugmelding naar een getest wijzigingsvoorstel en een pull request na goedkeuring.",
      },
      ticketQuestions: {
        title: "Slack- & Jira-ticketvragen",
        desc: "Vraag wat openstaat of op goedkeuring wacht — zonder per ongeluk een nieuw ticket aan te maken.",
      },
      approvedReplies: {
        title: "Goedgekeurde Freshdesk- & Jira-antwoorden",
        desc: "Stel op met AI en keur precies goed wat naar een klant van je organisatie of als interne notitie gaat.",
      },
      remediation: {
        title: "Van supportticket naar geteste fix",
        desc: "Maak van een supportticket een geteste codewijziging, geleverd als pull request die je team goedkeurt — met de testresultaten erbij.",
      },
      approvalLedger: {
        title: "AI-goedkeuringsoverzicht",
        desc: "Elke AI-ondersteunde actie is vastgelegd: wie keurde goed, vanuit welk kanaal, op welke gronden, en wat er gebeurde.",
      },
      deployment: {
        title: "Gecontroleerde deployments",
        desc: "Zet promoties achter menselijke goedkeuring, met live rollout-status waar Argo / Kubernetes is verbonden.",
      },
      hrGoverned: {
        title: "HR-vragen met de juiste rechten",
        desc: "Medewerkers van je organisatie vragen naar verlof, een loonstrook of een procedure. Een routine-antwoord gaat direct; een gevoelig antwoord wacht op een toegewezen beoordelaar en een tweede persoon die goedkeurt.",
      },
    },
  },
  integrations: {
    eyebrow: "Integraties",
    title: "Goedkeuringen en antwoorden waar je team",
    highlight: "al werkt",
    subtitle:
      "Meldingen ontvangen, ticketvragen beantwoorden, goedkeuringen afhandelen en fixes voorbereiden — zonder van tool te wisselen. Elke koppeling werkt met eigen inloggegevens, blijft binnen de gegevens van je eigen organisatie, en laat per onderdeel zien of het werkt in plaats van één groen vinkje.",
    relatedTitle: "Bekijk integraties",
    ctaPrimary: "Begin gratis",
    ctaSecondary: "Boek een demo",
    ctaTitle: "Verbind de tools die je al gebruikt",
    ctaDescription:
      "Verbind één integratie gratis bij de start, of boek een demo om gegoverneerde reads en writes over je stack te zien.",
    items: {
      slack: {
        title: "Slack",
        desc: "Intake, ticketvragen, goedkeuringen en beslis-kaarten — direct in de thread.",
      },
      teams: {
        title: "Microsoft Teams",
        desc: "Intake, vragen en goedkeuringen vanuit Teams, op dezelfde manier gegoverneerd als Slack.",
      },
      jira: {
        title: "Jira",
        desc: "Gegoverneerd issues lezen, transities, labels en reacties, met capability-health.",
      },
      freshdesk: {
        title: "Freshdesk",
        desc: "Goedgekeurde klantantwoorden en two-way ticketsync, geschreven via een duurzame outbox.",
      },
      odoo: {
        title: "Odoo Helpdesk",
        desc: "Tickets aangemaakt en gekoppeld, stages teruggelezen, en chatterantwoorden die op goedkeuring wachten.",
      },
      topdesk: {
        title: "TOPdesk",
        desc: "Incidenten aangemaakt en gekoppeld, de caller bijgehouden, en antwoorden die op goedkeuring wachten.",
      },
      afas: {
        title: "AFAS Profit",
        desc: "Alleen-lezen HR-velden voor een gegoverneerd antwoord — verlofuren en een looncode, niets teruggeschreven.",
      },
      youforce: {
        title: "Visma Raet Youforce",
        desc: "Hetzelfde alleen-lezen HR-contract op de Youforce-endpoints die je zelf aanbiedt.",
      },
      github: {
        title: "GitHub",
        desc: "Codebase-analyse en door AI voorgestelde patches, geleverd als risico-gepoorte pull requests met bewijs erbij.",
      },
      gitlab: {
        title: "GitLab",
        desc: "De gegoverneerde patch-flow voor GitLab-repos: analyse, bewijs en risico-gepoorte merge requests.",
      },
      argo: {
        title: "Argo & Kubernetes",
        desc: "Rollout-status en gepoorte promotie, met promote / abort waar geconfigureerd.",
      },
      fcagent: {
        title: "FC Agent — private infrastructuur",
        desc: "Uitsluitend uitgaande connectiviteit: govern private Kubernetes, Argo, Jenkins en GitLab zonder ze bloot te stellen.",
      },
    },
  },
};
